// settings-context.tsx
// Centralized state management for settings - eliminates prop drilling

import React, { createContext, useContext, useState, useEffect, useCallback, useMemo } from 'react';
import {
  passkeyApi,
  billingDetailsApi,
  BillingDetails,
  paymentsApi,
  PaymentInfo,
} from '../../../api';
import { useAuth } from '../../../contexts/auth/auth-context';
import { usePayment } from '../../../contexts/payment-context';
import {
  SettingsContextValue,
  ProfileData,
  NotificationsData,
  AccountData,
  PlanName,
  PlanState,
} from './settings.types';

// ─────────────────────────────────────────────────────────────────────────────
// Context
// ─────────────────────────────────────────────────────────────────────────────

const SettingsContext = createContext<SettingsContextValue | null>(null);

// ─────────────────────────────────────────────────────────────────────────────
// Provider
// ─────────────────────────────────────────────────────────────────────────────

interface SettingsProviderProps {
  children: React.ReactNode;
  open: boolean;
}

export function SettingsProvider({ children, open }: SettingsProviderProps) {
  const { profile: authProfile } = useAuth();
  const { paymentInfo: cachedPaymentInfo, refreshPayment } = usePayment();

  // Data state
  const [profileData, setProfileData] = useState<ProfileData | null>(null);
  const [notificationsData, setNotificationsData] = useState<NotificationsData>({
    notification_method: null,
    sms_consent: false,
  });
  const [accountData, setAccountData] = useState<AccountData>({
    passkeys: [],
    auto_prompt_passkey: true,
  });
  const [billingDetails, setBillingDetails] = useState<BillingDetails | null>(null);
  const [paymentInfo, setPaymentInfo] = useState<PaymentInfo | null>(null);

  // Loading states
  const [isLoadingPasskeys, setIsLoadingPasskeys] = useState(false);
  const [isLoadingPayment, setIsLoadingPayment] = useState(false);
  const [isLoadingBillingDetails, setIsLoadingBillingDetails] = useState(false);
  const [hasPrefetched, setHasPrefetched] = useState(false);

  // ─────────────────────────────────────────────────────────────────────────────
  // Data Prefetching
  // ─────────────────────────────────────────────────────────────────────────────

  useEffect(() => {
    if (!open) {
      // Reset prefetch flag when modal closes so it refetches on next open
      setHasPrefetched(false);
      return;
    }

    // Only prefetch once per modal open
    if (hasPrefetched) return;

    // Use profile from auth context immediately (no loading needed)
    if (authProfile) {
      setProfileData(authProfile as ProfileData);
      setNotificationsData({
        notification_method: authProfile.notification_method || null,
        sms_consent: authProfile.sms_consent || false,
      });
      setAccountData((prev) => ({
        ...prev,
        auto_prompt_passkey: authProfile.auto_prompt_passkey ?? true,
      }));
    }

    // Seed payment from PaymentContext (already fetched at boot) instead of a redundant API call
    if (cachedPaymentInfo) {
      setPaymentInfo(cachedPaymentInfo);
    }

    // Prefetch remaining data in background
    const prefetchData = async () => {
      setIsLoadingPayment(!cachedPaymentInfo);
      setIsLoadingPasskeys(true);
      setIsLoadingBillingDetails(true);

      try {
        const [payment, passkeys, billing] = await Promise.all([
          cachedPaymentInfo
            ? Promise.resolve(cachedPaymentInfo).finally(() => setIsLoadingPayment(false))
            : paymentsApi
                .getMe()
                .catch((err: any) => {
                  console.error('Failed to load payment info:', err);
                  return null;
                })
                .finally(() => setIsLoadingPayment(false)),
          passkeyApi
            .listCredentials()
            .catch((err: any) => {
              console.error('Failed to load passkeys:', err);
              return [];
            })
            .finally(() => setIsLoadingPasskeys(false)),
          billingDetailsApi
            .get()
            .catch((err: any) => {
              console.error('Failed to load billing details:', err);
              return null;
            })
            .finally(() => setIsLoadingBillingDetails(false)),
        ]);

        setPaymentInfo(payment);
        setAccountData((prev) => ({
          ...prev,
          passkeys: passkeys || [],
        }));
        setBillingDetails(billing);
        setHasPrefetched(true);
      } catch (error) {
        console.error('Failed to prefetch settings data:', error);
      }
    };

    prefetchData();
  }, [open, hasPrefetched, authProfile]);

  // Keep profileData in sync with authProfile changes (for live updates)
  useEffect(() => {
    if (authProfile) {
      setProfileData(authProfile as ProfileData);
    }
  }, [authProfile]);

  // ─────────────────────────────────────────────────────────────────────────────
  // Derived Values
  // ─────────────────────────────────────────────────────────────────────────────

  const planName: PlanName = useMemo(() => {
    const raw = (paymentInfo?.plan_name ?? '').toLowerCase();
    return raw === 'north_star' || raw === 'north star' ? 'North Star' : 'Sanctum';
  }, [paymentInfo]);

  const planState: PlanState = useMemo(() => {
    return paymentInfo?.is_on_trial ? 'trial' : 'active';
  }, [paymentInfo]);

  const trialEnd = paymentInfo?.trial_end;

  // ─────────────────────────────────────────────────────────────────────────────
  // Actions
  // ─────────────────────────────────────────────────────────────────────────────

  const updateBillingDetails = useCallback((details: BillingDetails) => {
    setBillingDetails(details);
  }, []);

  const refetchPaymentInfo = useCallback(async () => {
    setIsLoadingPayment(true);
    try {
      // Use PaymentContext's refresh which updates the global cache
      await refreshPayment();
    } catch (err: any) {
      console.error('Failed to refetch payment info:', err);
    } finally {
      setIsLoadingPayment(false);
    }
  }, [refreshPayment]);

  // Keep paymentInfo in sync with PaymentContext changes
  useEffect(() => {
    if (cachedPaymentInfo) {
      setPaymentInfo(cachedPaymentInfo);
    }
  }, [cachedPaymentInfo]);

  // ─────────────────────────────────────────────────────────────────────────────
  // Context Value
  // ─────────────────────────────────────────────────────────────────────────────

  const value: SettingsContextValue = useMemo(
    () => ({
      // Data
      profileData,
      notificationsData,
      accountData,
      billingDetails,
      paymentInfo,

      // Derived
      planName,
      planState,
      trialEnd,

      // Loading
      isLoadingPasskeys,
      isLoadingPayment,
      isLoadingBillingDetails,
      hasPrefetched,

      // Actions
      updateBillingDetails,
      refetchPaymentInfo,
    }),
    [
      profileData,
      notificationsData,
      accountData,
      billingDetails,
      paymentInfo,
      planName,
      planState,
      trialEnd,
      isLoadingPasskeys,
      isLoadingPayment,
      isLoadingBillingDetails,
      hasPrefetched,
      updateBillingDetails,
      refetchPaymentInfo,
    ]
  );

  return <SettingsContext.Provider value={value}>{children}</SettingsContext.Provider>;
}

// ─────────────────────────────────────────────────────────────────────────────
// Hook
// ─────────────────────────────────────────────────────────────────────────────

export function useSettingsContext(): SettingsContextValue {
  const context = useContext(SettingsContext);
  if (!context) {
    throw new Error('useSettingsContext must be used within a SettingsProvider');
  }
  return context;
}
